// CampPickerModal — second-step chooser rendered when a guest picks a
// parent-category property (currently only "Bivouacs La Belle Étoile",
// which hosts three distinct camps). Multi-select — the guest can
// pick any combination of the available camps and every choice is
// persisted on the block.

import { useMemo, useState, useEffect } from "react";
import { Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getHotelDetails } from "@/lib/hotelDetails";
import { getCampsForHotel } from "@/lib/bookingCamps";

const pick = (obj, lang) => (obj ? obj[lang] || obj.en || obj.fr || obj.es || "" : "");

const COPY = {
  title: {
    en: "Choose your camps",
    fr: "Choisir vos campements",
    es: "Elige tus campamentos",
  },
  subtitle: {
    en: "Each camp is a distinct experience — pick every camp you'd like to include in this enquiry. You can select any combination.",
    fr: "Chaque camp est une expérience distincte — sélectionnez chaque campement que vous souhaitez inclure dans cette demande. Toute combinaison est possible.",
    es: "Cada campamento es una experiencia distinta — selecciona todos los campamentos que quieras incluir en esta solicitud. Puedes elegir cualquier combinación.",
  },
  cancel: {
    en: "Cancel",
    fr: "Annuler",
    es: "Cancelar",
  },
  continue: {
    en: "Continue",
    fr: "Continuer",
    es: "Continuar",
  },
  emptyHint: {
    en: "Pick at least one camp to continue.",
    fr: "Choisissez au moins un camp pour continuer.",
    es: "Elige al menos un campamento para continuar.",
  },
  selectedCount: {
    en: (n) => (n === 1 ? "1 camp selected" : `${n} camps selected`),
    fr: (n) => (n === 1 ? "1 camp sélectionné" : `${n} camps sélectionnés`),
    es: (n) => (n === 1 ? "1 campamento seleccionado" : `${n} campamentos seleccionados`),
  },
};

const CampPickerModal = ({
  open,
  hotelSlug,
  initialCampSlugs,
  onClose,
  onConfirm,
  lang = "en",
}) => {
  const camps = useMemo(() => getCampsForHotel(hotelSlug || ""), [hotelSlug]);
  const campMeta = useMemo(() => {
    if (!hotelSlug) return {};
    const details = getHotelDetails(hotelSlug);
    const map = {};
    (details.camps || []).forEach((c) => {
      map[c.slug] = c;
    });
    return map;
  }, [hotelSlug]);

  // Multi-select — a Set of picked camp slugs.
  const [selected, setSelected] = useState(() => new Set(initialCampSlugs || []));

  // Reset selection whenever the modal is (re)opened for a new context.
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialCampSlugs || []));
    }
  }, [open, initialCampSlugs]);

  if (!hotelSlug || camps.length === 0) return null;

  const toggle = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    // Preserve the source order (as declared in bookingCamps.js) so the
    // downstream summary line reads Traditional · Luxe · Mini rather
    // than the click order.
    const orderedSlugs = camps.map((c) => c.slug).filter((s) => selected.has(s));
    onConfirm?.(orderedSlugs);
  };

  const count = selected.size;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
      <DialogContent
        className="max-w-3xl bg-[#FDFBF7] border-none p-0 gap-0 overflow-hidden"
        data-testid="camp-picker-modal"
      >
        <DialogHeader className="px-6 md:px-8 pt-6 md:pt-8 pb-4 md:pb-5 border-b border-[#2C2621]/10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#A07042]">
            Bivouacs La Belle Étoile
          </p>
          <DialogTitle className="font-serif-x text-2xl md:text-3xl text-[#2C2621] mt-1 leading-tight">
            {pick(COPY.title, lang)}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5C5248] leading-relaxed mt-2 max-w-2xl">
            {pick(COPY.subtitle, lang)}
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 md:px-6 py-5 md:py-6 max-h-[65vh] overflow-y-auto">
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {camps.map((c) => {
              const meta = campMeta[c.slug] || {};
              const isSelected = selected.has(c.slug);
              return (
                <li key={c.slug}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isSelected}
                    onClick={() => toggle(c.slug)}
                    data-testid={`camp-picker-option-${c.slug}`}
                    className={`group w-full text-left transition-all duration-200 border-2 ${
                      isSelected
                        ? "border-[#C16542] shadow-md shadow-[#C16542]/10"
                        : "border-[#2C2621]/10 hover:border-[#2C2621]/40"
                    }`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#2C2621]/5">
                      {meta.image ? (
                        <img
                          src={meta.image}
                          alt={pick(c.label, lang)}
                          loading="lazy"
                          decoding="async"
                          className={`w-full h-full object-cover transition-transform duration-500 ${
                            isSelected ? "scale-105" : "group-hover:scale-105"
                          }`}
                        />
                      ) : null}
                      {isSelected ? (
                        <div
                          className="absolute top-2.5 right-2.5 flex items-center justify-center w-7 h-7 rounded-full bg-[#C16542] text-white shadow-md"
                          data-testid={`camp-picker-option-${c.slug}-check`}
                        >
                          <Check className="w-4 h-4" strokeWidth={2.5} />
                        </div>
                      ) : null}
                    </div>

                    <div className="p-3 md:p-4 bg-white">
                      <p className="text-[9px] tracking-[0.28em] uppercase text-[#A07042]">
                        {pick(c.short, lang)}
                      </p>
                      <p className="font-serif-x text-lg text-[#2C2621] mt-1 leading-tight">
                        {pick(c.label, lang)}
                      </p>
                      {meta.eyebrow ? (
                        <p className="text-[11px] text-[#5C5248] mt-1.5 leading-snug line-clamp-2">
                          {pick(meta.eyebrow, lang)}
                        </p>
                      ) : null}
                      {meta.price ? (
                        <p className="text-[11px] text-[#C16542] font-medium mt-2 tracking-tight">
                          {pick(meta.price, lang)}
                        </p>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="px-6 md:px-8 py-4 md:py-5 bg-white border-t border-[#2C2621]/10 flex flex-wrap items-center justify-between gap-3">
          <p
            className={`text-[11px] tracking-[0.18em] uppercase ${
              count > 0 ? "text-[#5C5248]" : "text-[#5C5248]/60 italic"
            }`}
            data-testid="camp-picker-status"
          >
            {count > 0 ? COPY.selectedCount[lang]?.(count) || COPY.selectedCount.en(count) : pick(COPY.emptyHint, lang)}
          </p>
          <div className="flex items-center gap-3 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[#5C5248] hover:text-[#2C2621] px-3 py-2 transition-colors"
              data-testid="camp-picker-cancel"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.7} />
              {pick(COPY.cancel, lang)}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={count === 0}
              className="inline-flex items-center gap-2 bg-[#C16542] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#a04d2f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="camp-picker-confirm"
            >
              {pick(COPY.continue, lang)}
              <Check className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampPickerModal;
